/**
 * The unified pipeline record — one enquiry, first contact through close.
 *
 * Replaces the separate Lead and Deal shapes. Nothing was dropped in the merge,
 * so this is genuinely the union of both: the qualification fields the enquiry
 * half carried, and the commercial fields the deal half carried, on one object.
 */

/** Which half of the pipeline a stage sits in. */
export type PipelineSide = 'lead' | 'deal';

/** The five-value legacy status. DERIVED from `stage` — never write it. */
export type PipelineStatus = 'new' | 'contacted' | 'interested' | 'converted' | 'rejected';

export interface PipelineStage {
  key: string;
  label: string;
  count: number;
  side: PipelineSide;
}

export interface PipelineStagesResponse {
  stages: PipelineStage[];
  /** What the dashboard used to get from two separate menus. */
  totals: { leads: number; deals: number; all?: number };
  total?: number;
}

export interface UserRef {
  _id: string;
  display_name?: string;
  username?: string;
}

/** One product line — mirrors the backend `pipelineLineSchema` (db-mongo.js). */
export interface PipelineLine {
  productId?: string | null;
  variantId?: string | null;
  /** Read-only snapshot of the catalog product's own name at add time. */
  productName?: string;
  /** Read-only snapshot of the catalog product's grade at add time. */
  grade?: string;
  /** Free text for a manual/custom line only — a catalog-added line leaves this empty. */
  description?: string;
  quantity: number;
  unitPrice: number;
  discount?: number;
  discountType?: 'fixed' | 'percent';
  taxProfileId?: string | null;
  cgstRate?: number;
  sgstRate?: number;
  igstRate?: number;
  lineTotal?: number;
  notes?: string;
  productCode?: string;
  unitId?: string | null;
  unit?: string;
  capacity?: string;
  packSize?: string;
  qtyPerPack?: number;
  totalPackSize?: string;
  volumeLiters?: number;
  /** HSN/SAC snapshot at add time — see the backend schema comment. */
  hsnSac?: string;
  mrp?: number;
  basePrice?: number;
}

export interface PipelineRecord {
  _id: string;
  id: string;
  /** An IndiaMART enquiry from a customer already in the CRM, given as a new lead (2026-10-24). */
  existing_customer?: boolean;
  related_record_id?: string | null;
  related_owner_name?: string;
  related_stage?: string;

  /** Short unique lead number — shown as "L-0123". */
  lead_no?: number | null;
  /** Further BDMs working this record with its owner (a shared lead). */
  co_assignees?: string[];
  /** Detail route only, for a shared record: who is on it and who last worked on it. */
  sharing?: import('./components/SharedRecordBanner').RecordSharing | null;

  // ── the one lifecycle field ────────────────────────────────────────────────
  /** snake_case key. The label is looked up for display; never compare labels. */
  stage: string;
  stage_updated_at?: string | null;
  /** Derived from `stage`. Present for the reports that still read it. */
  status: PipelineStatus;
  sub_status?: string;

  // ── identity ───────────────────────────────────────────────────────────────
  name: string;
  clientType?: string;
  title?: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  emails?: string[];
  business_name?: string;
  service_required?: string;
  address?: string;
  website?: string;
  billingAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  shippingAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  saveShippingAsDefault?: boolean;

  /** Who owns it — the BDM who self-assigned. Distinct from who works it. */
  user_id?: UserRef | string | null;
  /** Who works it now. A record can be handed on without changing ownership. */
  assigned_to?: UserRef | string | null;
  created_by?: string | null;
  contactId?: string | null;
  branchId?: string | null;

  // ── customer purchase order ────────────────────────────────────────────────
  // Opt-in: `hasCustomerPo` is the rep's declaration that this order is
  // PO-backed, and only then is `customerPoNumber` required before the record
  // may reach Order Confirmed. Enforced server-side in
  // src/shared/sales/customer-po.js — the form's own check is a courtesy.
  /** Whether a Sales Order exists for this record — i.e. whether Dispatch can
   *  see it. A stage no longer creates one, so a record can reach Order
   *  Confirmed with nothing behind it. Supplied by GET /api/pipeline/:id. */
  hasSalesOrder?: boolean;
  /** A quotation has been ISSUED against this record, which makes it a
   *  read-only enquiry record. Supplied by GET /api/pipeline/:id — see
   *  issuedQuotationForRecord in src/api-sales/order-from-pipeline.js. */
  hasIssuedQuotation?: boolean;
  issuedQuotationNumber?: string;
  issuedQuotationId?: string;
  hasCustomerPo?: boolean;
  customerPoNumber?: string;
  /** ISO date string, or null. Sent as null to clear it. */
  customerPoDate?: string | null;
  customerPoAttachments?: { url: string; name: string }[];

  // ── source & SLA ───────────────────────────────────────────────────────────
  source?: string;
  lead_source_channel?: string;
  niche?: string;
  location?: string;
  industry?: string;

  // ── commercial ─────────────────────────────────────────────────────────────
  value?: number;
  currency?: string;
  probability?: number;
  priority?: string;
  expected_close?: string | null;
  lostReasonId?: string | null;
  lostReasonNote?: string;
  // ── contact outcome & retargeting ──────────────────────────────────────────
  lead_disposition?: string;
  disposition_reason?: string;
  disposition_notes?: string;
  retargeting_audience?: 'high_intent' | 'callback_pipeline' | 'lost_retargetable' | 'suppressed_junk' | 'unclassified';
  disposition_updated_at?: string | null;
  disposition_updated_by?: UserRef | string | null;
  wonAt?: string | null;
  closedAt?: string | null;
  assignment_status?: 'none' | 'pending' | 'accepted' | 'rejected';
  assigned_by?: string | null;
  assignment_note?: string;

  /** Cross-BDM contact-ownership reassignment awaiting admin approval — see
   *  POST /api/pipeline/:id/reassignment/:decision. A distinct concept from
   *  assignment_status above (a peer accepting a hand-off someone else
   *  decided); this is an admin deciding before any assignment happens. */
  reassignmentRequest?: {
    toUserId?: UserRef | string | null;
    contactId?: string | null;
    requestedBy?: string | null;
    requestedAt?: string | null;
    status?: 'pending' | 'approved' | 'rejected' | null;
    decidedBy?: string | null;
    decidedAt?: string | null;
  } | null;

  // ── products ───────────────────────────────────────────────────────────────
  lines?: PipelineLine[];
  subTotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  roundOff?: number;
  documentDiscountType?: 'percent' | 'fixed';
  documentDiscountValue?: number;

  // ── qualification ──────────────────────────────────────────────────────────
  qualification_product?: string;
  qualification_product_id?: string | null;
  qualification_quantity?: string;
  current_supplier?: string;
  current_buying_price?: number | null;
  required_specification?: string;
  required_packaging?: string;
  delivery_location?: string;
  monthly_requirement?: string;
  purchase_frequency?: string;
  decision_maker_name?: string;
  expected_purchase_date?: string | null;
  qualification_payment_terms?: string;
  existing_brand?: string;
  competitor?: string;
  budget_target_price?: number | null;
  sample_trial_requested?: boolean;

  // ── follow-up ──────────────────────────────────────────────────────────────
  follow_up_date?: string | null;
  follow_up_time?: string;
  notes?: string;

  additionalInfoValues?: { fieldId?: string | null; key: string; value: string }[];

  created_at?: string;
  updated_at?: string | null;
}

export interface PipelineFilters {
  search?: string;
  /** A single stage, or `side` for the whole lead/deal half. */
  stage?: string;
  side?: PipelineSide;
  status?: PipelineStatus;
  source?: string;
  owner?: string;
  startDate?: string;
  endDate?: string;
  branchId?: string;
  retargeting?: string;
  disposition?: string;
  /** Drop records that are already Closed Won / Closed Lost. For pickers that
   *  attach something to a record — a closed record is never the answer. */
  open?: boolean;
  /** Every record at an earlier stage than this one, by ordinal — e.g.
   *  'order_confirmed' for the dashboard's Open Enquiries definition. */
  beforeStage?: string;
  /** A lost_reason MasterItem id — only meaningful alongside stage=closed_lost. */
  lostReason?: string;
  /** Only the caller's own records. Resolved server-side from the session, and
   *  ignored for roles that already see everything. */
  mine?: boolean;
  /** Only records with an open cross-BDM reassignment request — meaningful
   *  for admin-tier viewers only, everyone else's own scope already excludes
   *  these (see reassignmentRequest, PipelineRecord). */
  pendingReassignment?: boolean;
  /** Server-allowlisted sort key (PIPELINE_SORTS, src/api-pipeline/index.js). */
  sort?: PipelineSort;
  limit: number;
  offset: number;
}

export type PipelineSort = 'recent' | 'name_asc' | 'name_desc' | 'value_asc' | 'value_desc' | 'date_asc' | 'date_desc' | 'leadno_asc' | 'leadno_desc';

/** "L-0123" — the record's short unique lead number ('' until it has one). */
export const leadNumberLabel = (n?: number | null) => (n && n > 0 ? `L-${String(n).padStart(4, '0')}` : '');

export interface PipelineListResponse {
  items: PipelineRecord[];
  total: number;
  limit: number;
  offset: number;
}

/** What a stage move returns, so the caller can report what actually happened. */
export interface StageMoveResult {
  ok: boolean;
  error?: string;
  from?: string;
  to?: string;
  changed?: boolean;
  customerSynced?: boolean;
}

/** Resolve the display name off a populated-or-not user reference. */
export function userName(ref: UserRef | string | null | undefined): string {
  if (!ref) return 'Unassigned';
  if (typeof ref === 'string') return ref;
  return ref.display_name || ref.username || 'Unassigned';
}

export interface PipelineRepStat {
  id: string;
  name: string;
  username: string;
  email: string;
  role: string;
  department: string;
  totalDeals: number;
  openDeals: number;
  wonDeals: number;
  lostDeals: number;
  openValue: number;
  wonValue: number;
  lostValue: number;
  winRate: number;
  avgDealSize: number;
  lastWorkedAt: string | null;
}

export interface PipelineRepSummaryResponse {
  reps: PipelineRepStat[];
  totals: {
    totalDeals: number;
    openDeals: number;
    wonDeals: number;
    lostDeals: number;
    openValue: number;
    wonValue: number;
    lostValue: number;
    winRate: number;
    repCount: number;
  };
}
